"use client";

import * as React from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

// ============================================
// TYPES
// ============================================
export type StepStatus = "pending" | "active" | "completed" | "error";
export type StepperVariant = "default" | "circles" | "dots";
export type StepperOrientation = "horizontal" | "vertical";

export interface StepItem {
  /** Step title */
  title: string;
  /** Step description */
  description?: string;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Step status */
  status?: StepStatus;
}

export interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of steps */
  steps: StepItem[];
  /** Current active step index (0-based) */
  activeStep: number;
  /** Called when a step is clicked */
  onStepClick?: (step: number) => void;
  /** Orientation */
  orientation?: StepperOrientation;
  /** Visual variant */
  variant?: StepperVariant;
  /** Allow clicking on steps to navigate */
  allowClickNavigation?: boolean;
  /** Show step numbers in indicators */
  showStepNumbers?: boolean;
}

// ============================================
// COMPONENT
// ============================================
const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      className,
      steps,
      activeStep,
      onStepClick,
      orientation = "horizontal",
      variant = "default",
      allowClickNavigation = false,
      showStepNumbers = true,
      ...props
    },
    ref
  ) => {
    const getStepStatus = (index: number, step: StepItem): StepStatus => {
      if (step.status) return step.status;
      if (index < activeStep) return "completed";
      if (index === activeStep) return "active";
      return "pending";
    };

    const isVertical = orientation === "vertical";

    const renderIndicator = (index: number, status: StepStatus, step: StepItem) => {
      const baseClasses = cn(
        styleDrivers.stepper.indicator,
        status === "active" && styleDrivers.stepper.indicatorActive,
        status === "completed" && styleDrivers.stepper.indicatorCompleted,
        status === "error" && styleDrivers.stepper.indicatorError
      );

      if (variant === "dots") {
        return (
          <div
            className={cn(
              "h-3 w-3 rounded-full transition-colors",
              status === "pending" && "bg-muted",
              status === "active" && "bg-primary",
              status === "completed" && "bg-primary",
              status === "error" && "bg-destructive"
            )}
          />
        );
      }

      if (step.icon) {
        return <div className={baseClasses}>{step.icon}</div>;
      }

      if (status === "completed") {
        return (
          <div className={baseClasses}>
            <Check className="h-4 w-4" />
          </div>
        );
      }

      if (status === "error") {
        return (
          <div className={baseClasses}>
            <X className="h-4 w-4" />
          </div>
        );
      }

      if (showStepNumbers) {
        return <div className={baseClasses}>{index + 1}</div>;
      }

      return <div className={baseClasses} />;
    };

    const handleStepClick = (index: number) => {
      if (allowClickNavigation && onStepClick) {
        onStepClick(index);
      }
    };

    return (
      <div
        ref={ref}
        data-slot="stepper"
        data-orientation={orientation}
        className={cn(
          isVertical ? styleDrivers.stepper.rootVertical : styleDrivers.stepper.root,
          className
        )}
        role="list"
        aria-label="Progress steps"
        {...props}
      >
        {steps.map((step, index) => {
          const status = getStepStatus(index, step);
          const isLast = index === steps.length - 1;
          const isClickable = allowClickNavigation && onStepClick;

          return (
            <React.Fragment key={index}>
              <div
                className={cn(
                  isVertical ? styleDrivers.stepper.stepVertical : styleDrivers.stepper.step,
                  isClickable && "cursor-pointer"
                )}
                role="listitem"
                aria-current={status === "active" ? "step" : undefined}
                onClick={() => handleStepClick(index)}
              >
                {renderIndicator(index, status, step)}
                <div className={styleDrivers.stepper.content}>
                  <span
                    className={cn(
                      styleDrivers.stepper.title,
                      status === "pending" && "text-muted-foreground"
                    )}
                  >
                    {step.title}
                  </span>
                  {step.description && (
                    <span className={styleDrivers.stepper.description}>
                      {step.description}
                    </span>
                  )}
                </div>
              </div>

              {!isLast && (
                <div
                  className={cn(
                    isVertical
                      ? styleDrivers.stepper.connectorVertical
                      : styleDrivers.stepper.connector,
                    index < activeStep && styleDrivers.stepper.connectorActive
                  )}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
);

Stepper.displayName = "Stepper";

// ============================================
// EXPORTS
// ============================================
export { Stepper };
